import { useEffect, useRef, useState, useMemo } from "react";
import useDebounce from "../../app/hooks/useDebounce";

/** Lib Imports */
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import MolGrid, { Column } from "@moltech/molui/molgrid";
import { saveAs } from "file-saver";

/** Utilility */
import { formatDate } from "../../utils/common/dateFormat";

/** Components */
import Badges from "../../components/common/badges/Badges";
import NoRecordFound from "../../components/common/no-record-found/NoRecordFound";

/** RTK Query */
import {
  useLazyGetDataQuery,
  usePostFileDataMutation,
  usePostGridDataMutation,
} from "../../app/services/commonApi";

/** CSS Files */
import "./ApplicantReport.scss";

import moment from "moment";
import { Card } from "primereact/card";
import ChartWrapper from "../../components/common/Charts/ChartWrapper";
import { useMasterData } from "../../app/hooks/useMasterData";
import { useDependentData } from "../../app/hooks/useDependentData";
import { DependentDataCacheURL, MasterDataCacheURL } from "../../enums/CacheURL";

const renderStatus = (row) => (
  <Badges
    label={row.applicantStatusName}
    type={row.applicantStatusName}
    color={row.applicantStatusColor}
  />
);

const ApplicantReport = () => {
  const candidateReportRef = useRef();
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [gridData, setGridData] = useState([]);
  const [searchApplicantname, setSearchApplicantname] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(null);
  const debouncedSearch = useDebounce(searchApplicantname, 300);
  const [fetchGridData] = usePostGridDataMutation();
  const {data: triggerGetChannels} = useMasterData(MasterDataCacheURL.GetAllChannels);
  const [fetchFileData, { isLoading: isExporting }] = usePostFileDataMutation();
  const { data: sourceData, fetchData: fetchSources } = useDependentData(DependentDataCacheURL.GetAllSourceByChannelId,  selectedChannel, {skip: !selectedChannel})
  const [getApplicantsStatusCountByChannelAndSource, { data: statusData, reset, isLoading: isStatusCountLoading, isFetching: isStatusCountFetching }] = useLazyGetDataQuery();
  const [getApplicantsTrends, { data: trendApiData, reset: resetTrend, isLoading: isTrendLoading, isFetching: isTrendFetching }] = useLazyGetDataQuery();

  const channelName = useMemo(() => {
    if (!Array.isArray(triggerGetChannels)) return [];

    return triggerGetChannels.map(item => ({
      label: item.channelName,
      value: item.channelId
    }));
  }, [triggerGetChannels]);

  useEffect(() => {
    if (selectedChannel) {
      fetchSources();
    } else {
      setSelectedSource(null);
    }
  }, [selectedChannel]);

  const sourceName = useMemo(() => {
    if (!Array.isArray(sourceData)) return [];
  
    return sourceData.map((item) => ({
      label: item.sourceName,
      value: item.sourceId,
    }));
  }, [sourceData]);

  useEffect(() => {
    getApplicantReportList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const handleClear = () => {
    setSelectedChannel(null);
    setSelectedSource(null);
    setSelectedEndDate(null);
    setSelectedStartDate(null);
    setSelectedStatus(null);
    setGridData([]);
    reset();
    resetTrend();
  };

  const getApplicantReportList = () => {
    if (!selectedChannel || !selectedSource) return;
    fetchGridData({
      url: "Report/GetCandidateReportByChannelAndSource",
      params: {
        channelId: selectedChannel,
        sourceId: selectedSource,
        statusId: selectedStatus,
        startDate: selectedStartDate,
        endDate: selectedEndDate,
        name: debouncedSearch?.trim() || null,
      },
    }).then((res) => {
      const resultArray = res?.data?.data ?? [];
      setGridData(resultArray);
    });

    getApplicantsStatusCountByChannelAndSource({
      url: "/Analytics/GetApplicantsStatusCountByChannelAndSourceChart",
      method: "GET",
      data: {
        channelId: selectedChannel,
        sourceId: selectedSource,
        statusId: selectedStatus,
        startDate: selectedStartDate
          ? moment(selectedStartDate).utc().toISOString()
          : moment.utc("2000-01-01").startOf("day").toISOString(),
        endDate: selectedEndDate
          ? moment(selectedEndDate).utc().toISOString()
          : moment.utc().endOf("day").toISOString(),
      },
    });

    getApplicantsTrends({
      url: "/Analytics/GetAllApplicantsTrendsChart",
      method: "GET",
      data: {
        channelId: selectedChannel,
        sourceId: selectedSource,
        startDate: selectedStartDate
          ? moment(selectedStartDate).utc().toISOString()
          : moment.utc("2000-01-01").startOf("day").toISOString(),
        endDate: selectedEndDate
          ? moment(selectedEndDate).utc().toISOString()
          : moment.utc().endOf("day").toISOString(),
      },
    });
  };

  const handleSearch = async () => {
    if (!selectedChannel || !selectedSource) return;
    getApplicantReportList();
  };

  const handleDateRangeChange = (e) => {
    const [startDate, endDate] = e.value || [];
    setSelectedStartDate(startDate || null);
    setSelectedEndDate(endDate || null);
  };

  const handleExport = async () => {
    if (!selectedChannel && !selectedSource) return;

    const response = await fetchFileData({
      url: "/Report/ExportCandidateReport",
      data: {
        SourceId: selectedSource,
        ChannelId: selectedChannel,
        startDate: selectedStartDate,
        endDate: selectedEndDate,
      },
    });
    const fileData = response.data;
    const blob = new Blob([fileData]);

    // Generate dynamic filename
    const sourceLabel = sourceName.find(
      (s) => s.value === selectedSource,
    )?.label;
    const startDateStr = selectedStartDate
      ? formatDate(selectedStartDate, "DD-MM-YYYY")
      : "";
    const endDateStr = selectedEndDate
      ? formatDate(selectedEndDate, "DD-MM-YYYY")
      : "";

    let filename = "Candidate-Report";
    if (sourceLabel) filename += `-${sourceLabel.replace(/\s+/g, "-")}`;
    if (startDateStr && endDateStr) {
      filename += `-${startDateStr}-to-${endDateStr}`;
    } else if (startDateStr) {
      filename += `-from-${startDateStr}`;
    } else if (endDateStr) {
      filename += `-to-${endDateStr}`;
    }
    filename += ".xlsx";

    saveAs(blob, fileData.filename || filename);
  };

  const header = (
    <div className="custom-header-content mt-4">
      <div className="p-card-title ">
        Candidate Report
        <div className="header-items">
          <InputText
            placeholder="Search by candidate name"
            value={searchApplicantname}
            disabled={!selectedChannel && !selectedSource}
            onChange={(e) => {
              const value = e.target.value;
              setSearchApplicantname(value);
            }}
          />
          <Button
            label="Export Report"
            icon="pi pi-download"
            className="p-button-sm export-btn  btn secondary-btn"
            onClick={handleExport}
            disabled={isExporting || !gridData.length}
            loading={isExporting}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="applicant-report">
      <Card className="custom-card height-full-grid-with-searchbar">
        <div className="page-header">
          <div className="left-title-part">
            <h2 className="card-title mb-0">Candidate Report</h2>
          </div>
        </div>
        <div className="card-filter">
          <div className="applicant-filter">
            <div className="filter-fields">
              <Dropdown
                value={selectedChannel}
                options={channelName}
                optionLabel="label"
                optionValue="value"
                placeholder="Select channel"
                className="w-full"
                onChange={(e) => {
                  setSelectedChannel(e.value);
                  setSelectedSource(null);
                }}
              />
              <Dropdown
                value={selectedSource}
                options={sourceName}
                optionLabel="label"
                optionValue="value"
                placeholder="Candidate Source"
                className="w-full"
                onChange={(e) => setSelectedSource(e.value)}
              />
              <Calendar
                name="startDate"
                label="Date Range"
                placeholder="Select Date Range"
                dateFormat="dd-mm-yy"
                selectionMode="range"
                showIcon
                showButtonBar
                value={
                  selectedStartDate || selectedEndDate
                    ? [selectedStartDate, selectedEndDate]
                    : null
                }
                onChange={handleDateRangeChange}
              />
              <div className="btn-group-sec">
                <Button
                  label="Search"
                  icon="pi pi-search"
                  onClick={handleSearch}
                  className="p-button-sm btn secondary-btn "
                  disabled={!selectedChannel && !selectedSource}
                />
                <Button
                  label="Clear"
                  icon="pi pi-times"
                  className="p-button-sm p-button-secondary btn secondary-light-btn"
                  onClick={handleClear}
                />
              </div>
            </div>
          </div>
        </div>
      </Card>
      <div className="applicant-report-top-section">
        <div className="chart-row">
          <ChartWrapper
            title="Candidate Status Breakdown"
            type="pie"
            data={statusData?.data?.status || []}
            xKey="status"
            yKey="count"
            colorType="status"
            customOptions={{
              tooltip: {
                y: {
                  formatter: (val) =>
                    `${val} candidate${val !== 1 ? "s" : ""}`,
                },
              },
            }}
            height={300}
            loading={isStatusCountLoading || isStatusCountFetching}
          />

          <ChartWrapper
            title="Candidate Trend"
            type="donut"
            data={trendApiData?.data?.data || []}
            xKey="label"
            yKey="count"
            customOptions={{
              tooltip: {
                y: {
                  formatter: (val) =>
                    `${val} candidate${val !== 1 ? "s" : ""}`,
                },
              },
            }}
            centerLabel="Candidates"
            loading={isTrendLoading || isTrendFetching}

          />
        </div>
      </div>
      <Card className="custom-card applicant-report-section" header={header}>
        {gridData?.length ? (
          <MolGrid
            ref={candidateReportRef}
            paginator={false}
            scrollable
            scrollHeight="flex"
            filterDisplay="none"
            value={gridData}
          >
            <Column field="applicantName" header="Candidate Name" />
            <Column field="applicantEmail" header="Email Address" />
            <Column field="applicantPhone" header="Number" />
            <Column
              field="applicantStatusName"
              header="Status"
              sortable={false}
              body={renderStatus}
            />
            <Column field="channelName" header="Channel Name" />
            <Column field="sourceName" header="Source Name" />
            <Column
              field="applyDate"
              header="Applied Date"
              body={(rowData) => {
                return formatDate(rowData.applyDate);
              }}
            />
          </MolGrid>
        ) : (
          <NoRecordFound message="" />
        )}
      </Card>
    </div>
  );
};

export default ApplicantReport;
